/**
 * CORE-G-6 shared "actioned row disappears" hook.
 *
 * Chans's 2026-09-15 rebuild spec §7 G-6:
 *   "Actioned rows disappear top-to-bottom."
 *
 * When an operator picks Pause / Ignore / Snooze on a management row
 * (or Yes / No on a negatives row), the row must vanish IMMEDIATELY
 * — before the network round-trip finishes. Waiting for the write
 * to land, then invalidating the query, then re-rendering, is the
 * "did I click that or not?" pattern that erodes trust.
 *
 * How to use
 *
 *   const { visibleRows, resolve, isResolved } = useResolvedRows(rows, {
 *     getRowId: (row) => row.id,
 *   });
 *
 *   Every adopter renders `visibleRows` instead of the raw list. When
 *   the operator picks an action:
 *
 *     - Call `resolve(row.id)` first (or synchronously with the write).
 *       The row leaves `visibleRows` on the same tick.
 *     - Then fire the mutation. If it fails, the caller decides whether
 *       to restore the row (via the `restore` helper) or show a toast
 *       with a retry.
 *
 * The hook is deliberately dumb about the write itself — the mutation
 * that hits the audit log stays with the caller. This hook only owns
 * the "make the row disappear now" side of the flow.
 *
 * When the upstream `rows` prop changes (react-query refetch after a
 * successful mutation, for example), any resolved id that is NO LONGER
 * in the upstream list is dropped from the resolved set. That way a
 * refetch does not accidentally re-hide a row whose backend state has
 * changed to something new.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface UseResolvedRowsOptions<T, Id extends string = string> {
  /** Extract the row's stable id. */
  getRowId: (row: T) => Id;
}

export interface UseResolvedRowsResult<T, Id extends string = string> {
  /** Upstream rows minus the ones the operator has resolved locally. */
  visibleRows: T[];
  /**
   * Mark a row as resolved — the caller wired an action to this id.
   * The row disappears from `visibleRows` on the next render.
   */
  resolve: (id: Id) => void;
  /** Restore a resolved id — e.g. when a write failed and the row should reappear. */
  restore: (id: Id) => void;
  /** True when this id is in the local resolved set. */
  isResolved: (id: Id) => boolean;
  /** Drop every resolved id — e.g. when the operator changes filter. */
  clear: () => void;
}

export function useResolvedRows<T, Id extends string = string>(
  rows: readonly T[],
  { getRowId }: UseResolvedRowsOptions<T, Id>,
): UseResolvedRowsResult<T, Id> {
  const [resolvedIds, setResolvedIds] = useState<ReadonlySet<Id>>(
    () => new Set<Id>(),
  );

  // Keep the resolved set aligned with the upstream row set. If an id
  // stops being in `rows` (a refetch dropped it, or the row moved out
  // of the window), drop it from the resolved set so a future row with
  // the same id is not silently hidden. See docstring.
  const idSet = useMemo(() => {
    const s = new Set<Id>();
    for (const row of rows) s.add(getRowId(row));
    return s;
  }, [rows, getRowId]);

  // A ref lets us prune without re-running the effect from state churn.
  const resolvedRef = useRef(resolvedIds);
  resolvedRef.current = resolvedIds;

  useEffect(() => {
    const current = resolvedRef.current;
    let changed = false;
    const next = new Set<Id>();
    for (const id of current) {
      if (idSet.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setResolvedIds(next);
  }, [idSet]);

  const resolve = useCallback((id: Id) => {
    setResolvedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const restore = useCallback((id: Id) => {
    setResolvedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const isResolved = useCallback(
    (id: Id) => resolvedIds.has(id),
    [resolvedIds],
  );

  const clear = useCallback(() => {
    setResolvedIds((prev) => (prev.size === 0 ? prev : new Set<Id>()));
  }, []);

  const visibleRows = useMemo(
    () => rows.filter((row) => !resolvedIds.has(getRowId(row))),
    [rows, resolvedIds, getRowId],
  );

  return { visibleRows, resolve, restore, isResolved, clear };
}

// ---------------------------------------------------------------------------
// Adoption register — mirrors the G-5 register so both hooks track the same
// migration.
// ---------------------------------------------------------------------------

export interface ResolveAdoption {
  section: string;
  ticket: string;
  adopted: boolean;
  note?: string;
}

export const RESOLVED_ROWS_ADOPTED_BY: readonly ResolveAdoption[] = [
  {
    section: 'Negative Keyword Suggestions',
    ticket: 'CORE-M-03.c',
    adopted: true,
    note:
      'M-03 wrapper drives useResolvedRows on Yes/No (CORE-M-03.c, 2026-09-18).',
  },
  {
    section: 'Campaign Review',
    ticket: 'CORE-M-04',
    adopted: false,
    note: 'Shell today (Q-6). Adoption lands with the row list.',
  },
  {
    section: 'Ad Group Review',
    ticket: 'CORE-M-05',
    adopted: false,
    note: 'Shell today (Q-6). Adoption lands with the row list.',
  },
  {
    section: 'Ad Review',
    ticket: 'CORE-M-06.b',
    adopted: false,
    note: 'AdHealthTable has its own row shape; adoption lands with M-06.b.',
  },
  {
    section: 'Keyword Review',
    ticket: 'CORE-M-07',
    adopted: false,
    note: 'Shell today (Q-3). Adoption lands with the row list.',
  },
  {
    section: 'Budget Reallocation',
    ticket: 'CORE-M-08.b',
    adopted: false,
    note:
      'Interior BudgetReallocationTab has its own row shape; adoption lands with M-08.b.',
  },
];
